import { apis } from '../../../core/services/params';
import axiosInstance from '../../../core/services/axiosConfig';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import StitchLayout from '../../components/Crm/CrmLayout';
import { Spin, Button, DatePicker, Progress, Card, Statistic, Row, Col, Tag, Badge, Tooltip, Table } from 'antd';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import {
    ReloadOutlined,
    UserOutlined,
    PhoneOutlined,
    ClockCircleOutlined,
    AlertOutlined,
    CheckCircleOutlined,
    PhoneFilled,
    CustomerServiceOutlined,
    SoundOutlined,
    DisconnectOutlined,
    CalendarOutlined,
    CaretDownOutlined,
    TeamOutlined,
    ExclamationCircleOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
    RiseOutlined,
    FallOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import type { Moment } from 'moment';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

// Interfaces
interface MonitorData {
    ACW: number;
    Busy: number;
    Calling: number;
    CallsInQueue: number;
    LoggedIn: number;
    LoggedOut: number;
    LongestWaitTime: number;
    Pause: number;
    Ready: number;
    TotalAgents: number;
    UserId: number;
    activeIvrCalls: number;
    activeIvrCallsInbound: number;
}

interface AnalyticsData {
    AnsweredCalls: string;
    AverageAnswerTime: string;
    AverageHandlingTime: string;
    AverageHoldTime: string;
    AverageSpeedToAnswer: string;
    AverageTalkTime: string;
    AverageWrapupTime: string;
    CallType: string;
    CampaignName: string;
    SLAPercentage: string;
    SNO: number;
    TotalCalls: string;
    TotalInboundAnsweredCalls: string;
    TotalInboundCalls: string;
    TotalInboundUnAnsweredCalls: string;
    TotalOutboundAnsweredCalls: string;
    TotalOutboundCalls: string;
    TotalOutboundUnAnsweredCalls: string;
    UnansweredCalls: string;
}

interface TrendData {
    UnansweredCalls: string;
    TotalCalls: string;
    SNO: number;
    CallHour: string;
    DateType: string;
    AnsweredCalls: string;
}

interface PerformanceData {
    DailyValues: string;
    HourlyValues: string;
    SNO: number;
}

interface AgentData {
    AgentId: string;
    AgentMode: string;
    AgentName: string;
    AgentPhoneNumber: string;
    AgentSkills: string;
    AgentState: string;
    AgentUniqueId: number;
    CallFrom_SkillName: string;
    CallStatus: string;
    CallType: string;
    CampaignNameOfLastCall?: string;
    CustomerPhoneNumber?: string;
    HoldDuration: number;
    SessionCount: number;
    Since: number;
    UCID?: string;
    UserId: number;
    sip_location: string;
    sip_number: string;
}

interface DashboardState {
    monitor: MonitorData[];
    analytics: AnalyticsData[];
    trends: TrendData[];
    performance: PerformanceData[];
    agents: AgentData[];
}

// Chart Components
const DonutChart: React.FC<{ title: string; size: number; data: Array<{ name: string; value: number; color: string }> }> = ({ title, size, data }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    let current = 0;

    return (
        <div className="text-center">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-3">{title}</h3>
            <div className="relative mx-auto" style={{ width: size, height: size }}>
                <svg width={size} height={size} className="transform -rotate-90">
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={size / 2 - 20}
                        fill="none"
                        stroke="#f0f0f0"
                        strokeWidth="20"
                    />
                    {data.map((item, index) => {
                        const percentage = total > 0 ? (item.value / total) * 100 : 0;
                        const strokeDasharray = 2 * Math.PI * (size / 2 - 20);
                        const strokeDashoffset = strokeDasharray - (strokeDasharray * percentage) / 100;
                        const rotation = (current / total) * 360;
                        current += item.value;

                        return (
                            <circle
                                key={index}
                                cx={size / 2}
                                cy={size / 2}
                                r={size / 2 - 20}
                                fill="none"
                                stroke={item.color}
                                strokeWidth="20"
                                strokeDasharray={strokeDasharray}
                                strokeDashoffset={strokeDashoffset}
                                style={{
                                    transformOrigin: `${size / 2}px ${size / 2}px`,
                                    transform: `rotate(${rotation}deg)`,
                                }}
                            />
                        );
                    })}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-xl font-bold text-gray-900 dark:text-white">{total}</div>
                    <div className="text-xs text-gray-500">Total</div>
                </div>
            </div>
            <div className="mt-3 space-y-1">
                {data.map((item, index) => (
                    <div key={index} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                            <span>{item.name}</span>
                        </div>
                        <span className="font-semibold">{item.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const TrendChart: React.FC<{ title: string; data: Array<{ hour: string; today: number; yesterday: number }> }> = ({ title, data }) => {
    // Transform data for Recharts
    const chartData = data.map(item => ({
        hour: item.hour,
        Today: item.today,
        Yesterday: item.yesterday
    }));

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
                    <p className="font-semibold text-gray-800">{`Time: ${label}`}</p>
                    {payload.map((entry: any, index: number) => (
                        <p key={index} style={{ color: entry.color }} className="font-medium">
                            {`${entry.dataKey}: ${entry.value} calls`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-4 text-center">{title}</h3>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                        data={chartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 60,
                        }}
                        barCategoryGap="20%"
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="hour"
                            tick={{ fontSize: 12, fill: '#666' }}
                            axisLine={{ stroke: '#d1d5db' }}
                            tickLine={{ stroke: '#d1d5db' }}
                            label={{
                                value: 'Time (Hours)',
                                position: 'insideBottom',
                                offset: -10,
                                style: { textAnchor: 'middle', fontSize: '12px', fill: '#666' }
                            }}
                        />
                        <YAxis
                            tick={{ fontSize: 12, fill: '#666' }}
                            axisLine={{ stroke: '#d1d5db' }}
                            tickLine={{ stroke: '#d1d5db' }}
                            label={{
                                value: 'Call Volume',
                                angle: -90,
                                position: 'insideLeft',
                                style: { textAnchor: 'middle', fontSize: '12px', fill: '#666' }
                            }}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend
                            wrapperStyle={{
                                paddingTop: '20px',
                                fontSize: '14px',
                                fontWeight: 'bold'
                            }}
                        />
                        <Bar
                            dataKey="Today"
                            fill="#22c55e"
                            radius={[4, 4, 0, 0]}
                            name="Today"
                        />
                        <Bar
                            dataKey="Yesterday"
                            fill="#3b82f6"
                            radius={[4, 4, 0, 0]}
                            name="Yesterday"
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

const HorizontalBarChart: React.FC<{ title: string; height: number; data: Array<{ name: string; value: number; color: string }> }> = ({ title, height, data }) => {
    const maxValue = Math.max(...data.map(item => item.value));

    return (
        <div>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-3 text-center">{title}</h3>
            <div className="space-y-2">
                {data.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                        <div className="w-16 text-xs text-gray-600 dark:text-gray-300 truncate">{item.name}</div>
                        <div className="flex-1 relative">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                    className="h-full rounded-full transition-all duration-1000"
                                    style={{
                                        backgroundColor: item.color,
                                        width: maxValue > 0 ? `${(item.value / maxValue) * 100}%` : '0%'
                                    }}
                                ></div>
                            </div>
                        </div>
                        <div className="w-8 text-xs font-semibold text-gray-800 dark:text-white text-right">{item.value}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AnimatedCounter: React.FC<{ value: number }> = ({ value }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        let start = 0;
        const end = value;
        const duration = 800;
        const increment = end / (duration / 16);

        const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
                setDisplayValue(end);
                clearInterval(timer);
            } else {
                setDisplayValue(Math.floor(start));
            }
        }, 16);

        return () => clearInterval(timer);
    }, [value]);

    return <span>{displayValue}</span>;
};

// Date Range Picker Component
const DateRangePicker: React.FC<{ dateRange: [Moment, Moment]; onApply: (range: [Moment, Moment]) => void }> = ({ dateRange, onApply }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempRange, setTempRange] = useState<[Dayjs, Dayjs]>([dayjs(dateRange[0].toDate()), dayjs(dateRange[1].toDate())]);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Convert Dayjs to Moment
    const dayjsToMoment = (dayjsDate: Dayjs): Moment => {
        return moment(dayjsDate.toDate());
    };

    // Convert Moment to Dayjs
    const momentToDayjs = (momentDate: Moment): Dayjs => {
        return dayjs(momentDate.toDate());
    };

    const presets = [
        { label: 'Today', getDates: () => [moment().startOf('day'), moment().endOf('day')] as [Moment, Moment] },
        { label: 'Yesterday', getDates: () => [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')] as [Moment, Moment] },
        { label: 'Last 7 days', getDates: () => [moment().subtract(6, 'days').startOf('day'), moment().endOf('day')] as [Moment, Moment] },
        { label: 'Last 30 days', getDates: () => [moment().subtract(29, 'days').startOf('day'), moment().endOf('day')] as [Moment, Moment] },
        { label: 'This month', getDates: () => [moment().startOf('month'), moment().endOf('month')] as [Moment, Moment] },
    ];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Update tempRange when dateRange prop changes
    useEffect(() => {
        setTempRange([momentToDayjs(dateRange[0]), momentToDayjs(dateRange[1])]);
    }, [dateRange]);

    return (
        <div className="relative" ref={dropdownRef}>
            <Button
                icon={<CalendarOutlined />}
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2"
            >
                {dateRange[0].format('MMM DD')} - {dateRange[1].format('MMM DD')}
                <CaretDownOutlined />
            </Button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 p-4 w-96">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        {presets.map(preset => (
                            <Button
                                key={preset.label}
                                size="small"
                                onClick={() => {
                                    const newRange = preset.getDates();
                                    setTempRange([momentToDayjs(newRange[0]), momentToDayjs(newRange[1])]);
                                    onApply(newRange);
                                    setIsOpen(false);
                                }}
                                className="text-left justify-start"
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </div>
                    <RangePicker
                        value={tempRange}
                        onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                                setTempRange([dates[0], dates[1]]);
                            }
                        }}
                        className="w-full mb-3"
                    />
                    <div className="flex gap-2 justify-end">
                        <Button size="small" onClick={() => setIsOpen(false)}>Cancel</Button>
                        <Button
                            size="small"
                            type="primary"
                            onClick={() => {
                                const momentRange: [Moment, Moment] = [dayjsToMoment(tempRange[0]), dayjsToMoment(tempRange[1])];
                                onApply(momentRange);
                                setIsOpen(false);
                            }}
                        >
                            Apply
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Main Component
const OzonetelDashboard = () => {
    const [dateRange, setDateRange] = useState<[Moment, Moment]>([
        moment().startOf('day'),
        moment().endOf('day'),
    ]);

    const [data, setData] = useState<DashboardState>({
        monitor: [],
        analytics: [],
        trends: [],
        performance: [],
        agents: []
    });

    const [loading, setLoading] = useState({
        monitor: true,
        analytics: true,
        trends: true,
        performance: true,
        agents: true,
    });

    const [error, setError] = useState('');

    // Helper functions for agent status colors
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'READY': return '#52c41a';
            case 'BUSY': return '#fa8c16';
            case 'OFFLINE': return '#ff4d4f';
            default: return '#d9d9d9';
        }
    };

    const getCallStatusColor = (status: string) => {
        switch (status) {
            case 'answered': return '#52c41a';
            case 'not_answered': return '#ff4d4f';
            case 'agent_disconnect': return '#fa8c16';
            case 'Congestion': return '#722ed1';
            default: return '#d9d9d9';
        }
    };

    // Fetch monitor data
    const fetchMonitorData = async () => {
        setLoading(prev => ({ ...prev, monitor: true }));
        try {
            const response = await axiosInstance.post(apis.OZONETEL_DASHBOARD_API, {
                "apiId": 2502,
                "Type": 1,
                "FilterId": "74146",
                "Key": "",
                "ChannelType": "0",
                "CampaignType": "All",
                "UserId": "4805"
            });
            if (response.data.status && response.data.data) {
                setData(prev => ({ ...prev, monitor: response.data.data }));
            }
        } catch (err) {
            console.error('Failed to fetch monitor data:', err);
            setError('Failed to fetch agent monitoring data');
        } finally {
            setLoading(prev => ({ ...prev, monitor: false }));
        }
    };

    // Fetch analytics data
    const fetchAnalyticsData = async (range: [Moment, Moment] = dateRange) => {
        setLoading(prev => ({ ...prev, analytics: true }));
        setError('');
        try {
            const response = await axiosInstance.post(apis.OZONETEL_DASHBOARD_API, {
                "apiId": 105,
                "Type": "1",
                "FilterId": "296790",
                "CallType": "all",
                "DateTypeId": "0",
                "FromTime": range[0].format('YYYY-MM-DD HH:mm:ss'),
                "ToTime": range[1].format('YYYY-MM-DD HH:mm:ss'),
                "CampaignType": "All",
                "UserId": "4805"
            });
            if (response.data.status && response.data.data) {
                setData(prev => ({ ...prev, analytics: response.data.data }));
            }
        } catch (err) {
            console.error('Failed to fetch analytics data:', err);
            setError('Failed to fetch analytics data');
        } finally {
            setLoading(prev => ({ ...prev, analytics: false }));
        }
    };

    // Fetch performance data
    const fetchPerformanceData = async () => {
        setLoading(prev => ({ ...prev, performance: true }));
        try {
            const response = await axiosInstance.post(apis.OZONETEL_DASHBOARD_API, {
                "apiId": 147,
                "Type": "1",
                "FilterId": "0",
                "UserId": "4805",
                "CallType": "voice,outbound",
                "CampaignType": "All"
            });
            if (response.data.status && response.data.data) {
                setData(prev => ({ ...prev, performance: response.data.data }));
            }
        } catch (err) {
            console.error('Failed to fetch performance data:', err);
            setError('Failed to fetch performance data');
        } finally {
            setLoading(prev => ({ ...prev, performance: false }));
        }
    };

    // Fetch agent performance data
    const fetchAgentData = async () => {
        setLoading(prev => ({ ...prev, agents: true }));
        try {
            const response = await axiosInstance.post(apis.OZONETEL_DASHBOARD_API, {
                "apiId": 104,
                "Type": 1,
                "FilterId": "0",
                "State": "All",
                "Mode": "All",
                "CampaignType": "All",
                "UserId": "4805"
            });
            if (response.data.status && response.data.data) {
                setData(prev => ({ ...prev, agents: response.data.data }));
            }
        } catch (err) {
            console.error('Failed to fetch agent data:', err);
            setError('Failed to fetch agent performance data');
        } finally {
            setLoading(prev => ({ ...prev, agents: false }));
        }
    };

    // Fetch trend data for comparison
    const fetchTrendsData = async () => {
        setLoading(prev => ({ ...prev, trends: true }));
        try {
            // Get current day and yesterday data
            const [todayResponse, yesterdayResponse] = await Promise.all([
                axiosInstance.post(apis.OZONETEL_DASHBOARD_API, {
                    "apiId": 114,
                    "Type": 1,
                    "FilterId": "296790",
                    "CallType": "all",
                    "DateTypeId": 0,
                    "Flag": 1,
                    "CampaignType": "All",
                    "UserId": "4805"
                }),
                axiosInstance.post(apis.OZONETEL_DASHBOARD_API, {
                    "apiId": 114,
                    "Type": 1,
                    "FilterId": "296790",
                    "CallType": "all",
                    "DateTypeId": 1,
                    "Flag": 0,
                    "CampaignType": "All",
                    "UserId": "4805"
                })
            ]);

            const combinedData: TrendData[] = [];
            if (todayResponse.data.status && todayResponse.data.data) {
                combinedData.push(...todayResponse.data.data);
            }
            if (yesterdayResponse.data.status && yesterdayResponse.data.data) {
                combinedData.push(...yesterdayResponse.data.data);
            }

            setData(prev => ({ ...prev, trends: combinedData }));
        } catch (err) {
            console.error('Failed to fetch trends data:', err);
            setError('Failed to fetch trends data');
        } finally {
            setLoading(prev => ({ ...prev, trends: false }));
        }
    };

    const fetchAllData = () => {
        fetchMonitorData();
        fetchAnalyticsData();
        fetchTrendsData();
        fetchPerformanceData();
        fetchAgentData();
    };

    const handleDateRangeApply = (range: [Moment, Moment]) => {
        setDateRange(range);
        fetchAnalyticsData(range);
    };

    useEffect(() => {
        fetchAllData();
        const interval = setInterval(fetchMonitorData, 30000);
        return () => clearInterval(interval);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Process monitor data
    const agentStats = useMemo(() => {
        if (!data.monitor.length) return null;
        const monitor = data.monitor[0];
        return {
            totalAgents: monitor.TotalAgents,
            loggedIn: monitor.LoggedIn,
            ready: monitor.Ready,
            busy: monitor.Busy,
            calling: monitor.Calling,
            acw: monitor.ACW,
            pause: monitor.Pause,
            loggedOut: monitor.LoggedOut,
            callsInQueue: monitor.CallsInQueue,
            longestWaitTime: monitor.LongestWaitTime,
            activeIvrCalls: monitor.activeIvrCalls,
            activeIvrCallsInbound: monitor.activeIvrCallsInbound,
        };
    }, [data.monitor]);

    // Process analytics data
    const callStats = useMemo(() => {
        if (!data.analytics.length) return null;

        const totals = data.analytics.reduce((acc, item) => ({
            totalCalls: acc.totalCalls + parseInt(item.TotalCalls || '0'),
            answeredCalls: acc.answeredCalls + parseInt(item.AnsweredCalls || '0'),
            unansweredCalls: acc.unansweredCalls + parseInt(item.UnansweredCalls || '0'),
            inboundCalls: acc.inboundCalls + parseInt(item.TotalInboundCalls || '0'),
            outboundCalls: acc.outboundCalls + parseInt(item.TotalOutboundCalls || '0'),
            inboundAnswered: acc.inboundAnswered + parseInt(item.TotalInboundAnsweredCalls || '0'),
            outboundAnswered: acc.outboundAnswered + parseInt(item.TotalOutboundAnsweredCalls || '0'),
        }), {
            totalCalls: 0,
            answeredCalls: 0,
            unansweredCalls: 0,
            inboundCalls: 0,
            outboundCalls: 0,
            inboundAnswered: 0,
            outboundAnswered: 0,
        });

        const avgAnswerTime = data.analytics.reduce((sum, item) => sum + parseFloat(item.AverageAnswerTime || '0'), 0) / data.analytics.length;
        const avgHandlingTime = data.analytics.reduce((sum, item) => sum + parseFloat(item.AverageHandlingTime || '0'), 0) / data.analytics.length;
        const avgHoldTime = data.analytics.reduce((sum, item) => sum + parseFloat(item.AverageHoldTime || '0'), 0) / data.analytics.length;
        const avgSpeedToAnswer = data.analytics.reduce((sum, item) => sum + parseFloat(item.AverageSpeedToAnswer || '0'), 0) / data.analytics.length;
        const avgTalkTime = data.analytics.reduce((sum, item) => sum + parseFloat(item.AverageTalkTime || '0'), 0) / data.analytics.length;
        const avgWrapupTime = data.analytics.reduce((sum, item) => sum + parseFloat(item.AverageWrapupTime || '0'), 0) / data.analytics.length;
        const avgSLA = data.analytics.reduce((sum, item) => sum + parseFloat(item.SLAPercentage || '0'), 0) / data.analytics.length;

        return {
            ...totals,
            avgAnswerTime: avgAnswerTime.toFixed(1),
            avgHandlingTime: avgHandlingTime.toFixed(1),
            avgHoldTime: avgHoldTime.toFixed(1),
            avgSpeedToAnswer: avgSpeedToAnswer.toFixed(1),
            avgTalkTime: avgTalkTime.toFixed(1),
            avgWrapupTime: avgWrapupTime.toFixed(1),
            avgSLA: avgSLA.toFixed(1),
            answerRate: totals.totalCalls > 0 ? ((totals.answeredCalls / totals.totalCalls) * 100).toFixed(1) : '0',
        };
    }, [data.analytics]);

    const isLoading = loading.monitor || loading.analytics || loading.performance || loading.agents;

    // Process trend data for charts
    const trendStats = useMemo(() => {
        if (!data.trends.length) return null;

        const todayData = data.trends.filter(item => item.DateType === 'CurrentDay');
        const yesterdayData = data.trends.filter(item => item.DateType === 'YesterDay');

        // Create hourly comparison data
        const hourlyComparison = [];
        for (let hour = 9; hour <= 19; hour++) {
            const todayHour = todayData.find(item => parseInt(item.CallHour) === hour);
            const yesterdayHour = yesterdayData.find(item => parseInt(item.CallHour) === hour);

            hourlyComparison.push({
                hour: `${hour}:00`,
                today: todayHour ? parseInt(todayHour.TotalCalls) : 0,
                yesterday: yesterdayHour ? parseInt(yesterdayHour.TotalCalls) : 0,
                todayAnswered: todayHour ? parseInt(todayHour.AnsweredCalls) : 0,
                yesterdayAnswered: yesterdayHour ? parseInt(yesterdayHour.AnsweredCalls) : 0,
            });
        }

        const todayTotal = todayData.reduce((sum, item) => sum + parseInt(item.TotalCalls || '0'), 0);
        const yesterdayTotal = yesterdayData.reduce((sum, item) => sum + parseInt(item.TotalCalls || '0'), 0);
        const todayAnswered = todayData.reduce((sum, item) => sum + parseInt(item.AnsweredCalls || '0'), 0);
        const yesterdayAnswered = yesterdayData.reduce((sum, item) => sum + parseInt(item.AnsweredCalls || '0'), 0);

        const growthRate = yesterdayTotal > 0 ? (((todayTotal - yesterdayTotal) / yesterdayTotal) * 100).toFixed(1) : '0';
        const answerRateToday = todayTotal > 0 ? ((todayAnswered / todayTotal) * 100).toFixed(1) : '0';
        const answerRateYesterday = yesterdayTotal > 0 ? ((yesterdayAnswered / yesterdayTotal) * 100).toFixed(1) : '0';

        return {
            hourlyComparison,
            todayTotal,
            yesterdayTotal,
            todayAnswered,
            yesterdayAnswered,
            growthRate: parseFloat(growthRate),
            answerRateToday: parseFloat(answerRateToday),
            answerRateYesterday: parseFloat(answerRateYesterday),
        };
    }, [data.trends]);

    // Process performance data
    const performanceStats = useMemo(() => {
        if (!data.performance.length) return null;

        const perfData = data.performance[0];

        // Parse daily values JSON
        let dailyData;
        try {
            const parsed = JSON.parse(perfData.DailyValues);
            const headers = parsed.Headers;
            const row = parsed.Rows[0];
            dailyData = headers.reduce((obj: any, header: string, index: number) => {
                obj[header] = row[index];
                return obj;
            }, {});
        } catch (e) {
            console.error('Failed to parse daily performance data:', e);
            return null;
        }

        // Parse hourly values JSON
        let hourlyData: any[] = [];
        try {
            const parsed = JSON.parse(perfData.HourlyValues);
            const headers = parsed.Headers;
            hourlyData = parsed.Rows.map((row: any[]) => {
                return headers.reduce((obj: any, header: string, index: number) => {
                    obj[header] = row[index];
                    return obj;
                }, {});
            });
        } catch (e) {
            console.error('Failed to parse hourly performance data:', e);
        }

        // Calculate key performance metrics
        const slaPerformance = parseFloat(dailyData.SLA || '0');
        const avgHandlingTime = parseFloat(dailyData.AvgHandlingTime || '0');
        const callConnectionRatio = parseFloat(dailyData.CallConnectionRatio || '0');
        const callAbandonedRatio = parseFloat(dailyData.CallAbandonedRatio || '0');
        const avgWaitTime = parseFloat(dailyData.AvgWaitTime || '0');
        const longestQueueTime = parseInt(dailyData.LongestQueueTime || '0');
        const totalQueuedCalls = parseInt(dailyData.TotalQueuedCalls || '0');
        const wrapTimeExceeded = parseFloat(dailyData.WrapTimeExceeded || '0');

        // Transform hourly data for charts
        const hourlyChartData = hourlyData.map(item => ({
            hour: `${item.CallHour}:00`,
            calls: parseInt(item.TotalCalls || '0'),
            answered: parseInt(item.AnsweredCalls || '0'),
            abandoned: parseInt(item.AbandonedCalls || '0'),
            sla: parseFloat(item.SLA || '0'),
            avgHandling: parseFloat(item.AvgHandlingTime || '0'),
            connectionRatio: parseFloat(item.CallConnectionRatio || '0')
        }));

        return {
            daily: dailyData,
            hourly: hourlyData,
            hourlyChart: hourlyChartData,
            kpis: {
                slaPerformance,
                avgHandlingTime,
                callConnectionRatio,
                callAbandonedRatio,
                avgWaitTime,
                longestQueueTime,
                totalQueuedCalls,
                wrapTimeExceeded
            }
        };
    }, [data.performance]);

    // Process agent performance data
    const agentPerformanceStats = useMemo(() => {
        if (!data.agents.length) return null;

        const agentStats = {
            totalAgents: data.agents.length,
            ready: data.agents.filter(agent => agent.AgentState === 'READY').length,
            busy: data.agents.filter(agent => agent.AgentState === 'BUSY').length,
            offline: data.agents.filter(agent => agent.AgentState === 'OFFLINE').length,
            manual: data.agents.filter(agent => agent.AgentMode === 'MANUAL').length,
            inbound: data.agents.filter(agent => agent.AgentMode === 'INBOUND').length,
            answered: data.agents.filter(agent => agent.CallStatus === 'answered').length,
            notAnswered: data.agents.filter(agent => agent.CallStatus === 'not_answered').length
        };

        // Agent details with enhanced information
        const agentDetails = data.agents.map(agent => ({
            ...agent,
            timeSince: agent.Since ? Math.floor(agent.Since / 60) : 0, // Convert to minutes
            isActive: agent.AgentState === 'BUSY' || agent.AgentState === 'READY',
            skillsCount: agent.AgentSkills ? agent.AgentSkills.split(',').length : 0,
            statusColor: getStatusColor(agent.AgentState),
            callStatusColor: getCallStatusColor(agent.CallStatus)
        })).sort((a, b) => {
            // Sort by activity: READY first, then BUSY, then others
            const priority = { 'READY': 0, 'BUSY': 1, 'OFFLINE': 2 };
            return (priority[a.AgentState as keyof typeof priority] || 3) - (priority[b.AgentState as keyof typeof priority] || 3);
        });

        return {
            stats: agentStats,
            agents: agentDetails
        };
    }, [data.agents]);

    return (
        <StitchLayout>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-0.5">
                            📊 Ozonetel Call Center Dashboard
                        </h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Real-time monitoring & comprehensive analytics
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* <DateRangePicker dateRange={dateRange} onApply={handleDateRangeApply} /> */}
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchAllData}
                            loading={isLoading}
                            type="primary"
                        >
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Navigation CTAs */}
                <div className="mb-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
                    <div className="flex flex-wrap justify-center gap-3">
                        <Button
                            type="default"
                            size="small"
                            icon={<TeamOutlined />}
                            onClick={() => document.getElementById('live-status')?.scrollIntoView({ behavior: 'smooth' })}
                            className="border-blue-300 text-blue-600 hover:border-blue-500 hover:text-blue-700"
                        >
                            Live Status
                        </Button>
                        <Button
                            type="default"
                            size="small"
                            icon={<PhoneOutlined />}
                            onClick={() => document.getElementById('call-analytics')?.scrollIntoView({ behavior: 'smooth' })}
                            className="border-green-300 text-green-600 hover:border-green-500 hover:text-green-700"
                        >
                            Call Analytics
                        </Button>
                        <Button
                            type="default"
                            size="small"
                            icon={<ClockCircleOutlined />}
                            onClick={() => document.getElementById('time-metrics')?.scrollIntoView({ behavior: 'smooth' })}
                            className="border-orange-300 text-orange-600 hover:border-orange-500 hover:text-orange-700"
                        >
                            Time Metrics
                        </Button>
                        <Button
                            type="default"
                            size="small"
                            icon={<RiseOutlined />}
                            onClick={() => document.getElementById('call-trends')?.scrollIntoView({ behavior: 'smooth' })}
                            className="border-purple-300 text-purple-600 hover:border-purple-500 hover:text-purple-700"
                        >
                            Call Trends
                        </Button>
                        <Button
                            type="default"
                            size="small"
                            icon={<AlertOutlined />}
                            onClick={() => document.getElementById('performance-metrics')?.scrollIntoView({ behavior: 'smooth' })}
                            className="border-red-300 text-red-600 hover:border-red-500 hover:text-red-700"
                        >
                            Performance
                        </Button>
                        <Button
                            type="default"
                            size="small"
                            icon={<UserOutlined />}
                            onClick={() => document.getElementById('agent-performance')?.scrollIntoView({ behavior: 'smooth' })}
                            className="border-cyan-300 text-cyan-600 hover:border-cyan-500 hover:text-cyan-700"
                        >
                            Agents
                        </Button>
                    </div>
                </div>

                {error && (
                    <div className="text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center mb-4 text-sm">
                        {error}
                    </div>
                )}

                {/* Live Status */}
                {agentStats && (
                    <div id="live-status" className="mb-4 scroll-mt-4">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Live Status</h2>
                            <Badge status="processing" text="Auto-refreshing" />
                        </div>

                        <Row gutter={[12, 12]} className="mb-4">
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-blue-50 to-blue-100" bodyStyle={{ padding: '16px 12px' }}>
                                    <PhoneFilled className="text-2xl text-blue-600 mb-2" />
                                    <div className="text-xl font-bold text-blue-800 mb-1">
                                        <AnimatedCounter value={agentStats.activeIvrCalls} />
                                    </div>
                                    <div className="text-xs text-blue-600 font-semibold">Active IVR Calls</div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-yellow-50 to-amber-100" bodyStyle={{ padding: '16px 12px' }}>
                                    <ClockCircleOutlined className="text-2xl text-yellow-600 mb-2" />
                                    <div className="text-xl font-bold text-yellow-800 mb-1">
                                        <AnimatedCounter value={agentStats.callsInQueue} />
                                    </div>
                                    <div className="text-xs text-yellow-600 font-semibold">Calls in Queue</div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-green-50 to-green-100" bodyStyle={{ padding: '16px 12px' }}>
                                    <TeamOutlined className="text-2xl text-green-600 mb-2" />
                                    <div className="text-xl font-bold text-green-800 mb-1">
                                        <AnimatedCounter value={agentStats.loggedIn} />
                                    </div>
                                    <div className="text-xs text-green-600 font-semibold">Agents Online</div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-purple-50 to-purple-100" bodyStyle={{ padding: '16px 12px' }}>
                                    <CheckCircleOutlined className="text-2xl text-purple-600 mb-2" />
                                    <div className="text-xl font-bold text-purple-800 mb-1">
                                        <AnimatedCounter value={agentStats.ready} />
                                    </div>
                                    <div className="text-xs text-purple-600 font-semibold">Ready Agents</div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-orange-50 to-orange-100" bodyStyle={{ padding: '16px 12px' }}>
                                    <SoundOutlined className="text-2xl text-orange-600 mb-2" />
                                    <div className="text-xl font-bold text-orange-800 mb-1">
                                        <AnimatedCounter value={agentStats.busy} />
                                    </div>
                                    <div className="text-xs text-orange-600 font-semibold">Busy Agents</div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-red-50 to-red-100" bodyStyle={{ padding: '16px 12px' }}>
                                    <PauseCircleOutlined className="text-2xl text-red-600 mb-2" />
                                    <div className="text-xl font-bold text-red-800 mb-1">
                                        <AnimatedCounter value={agentStats.pause} />
                                    </div>
                                    <div className="text-xs text-red-600 font-semibold">On Break</div>
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={8}>
                                <Card className="h-full" bodyStyle={{ padding: '16px' }}>
                                    <DonutChart
                                        title="Agent Status Distribution"
                                        size={140}
                                        data={[
                                            { name: 'Ready', value: agentStats.ready, color: '#52c41a' },
                                            { name: 'Busy', value: agentStats.busy, color: '#ff4d4f' },
                                            { name: 'Calling', value: agentStats.calling, color: '#1890ff' },
                                            { name: 'ACW', value: agentStats.acw, color: '#faad14' },
                                            { name: 'Pause', value: agentStats.pause, color: '#722ed1' },
                                        ]}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} md={8}>
                                <HorizontalBarChart
                                    title="Agent Activity Levels"
                                    height={140}
                                    data={[
                                        { name: 'Ready', value: agentStats.ready, color: '#52c41a' },
                                        { name: 'Busy', value: agentStats.busy, color: '#ff4d4f' },
                                        { name: 'Calling', value: agentStats.calling, color: '#1890ff' },
                                        { name: 'ACW', value: agentStats.acw, color: '#faad14' },
                                        { name: 'Pause', value: agentStats.pause, color: '#722ed1' },
                                    ]}
                                />
                            </Col>
                            <Col xs={24} md={8}>
                                <Card className="h-full" bodyStyle={{ padding: '16px' }}>
                                    <div className="text-center">
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-3">
                                            Queue Status
                                        </h3>
                                        <div className="w-16 h-16 mx-auto mb-3 relative">
                                            <svg className="w-full h-full transform -rotate-90">
                                                <circle cx="32" cy="32" r="28" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                                                <circle
                                                    cx="32" cy="32" r="28" fill="none" stroke="#faad14" strokeWidth="6"
                                                    strokeLinecap="round"
                                                    strokeDasharray={2 * Math.PI * 28}
                                                    strokeDashoffset={2 * Math.PI * 28 - (2 * Math.PI * 28 * Math.min(agentStats.callsInQueue / 20, 1))}
                                                    className="transition-all duration-1000"
                                                />
                                            </svg>
                                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                <span className="text-lg font-bold">{agentStats.callsInQueue}</span>
                                                <span className="text-xs text-gray-500">queue</span>
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            Longest wait: {agentStats.longestWaitTime}s
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                )}

                {/* Call Analytics */}
                {callStats && (
                    <div id="call-analytics" className="mb-4 scroll-mt-4">
                        <div className="flex items-center gap-2 mb-3">
                            <PhoneOutlined className="text-green-500 text-lg" />
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Call Analytics</h2>
                        </div>

                        {/* Call Statistics Cards */}
                        <Row gutter={[12, 12]} className="mb-4">
                            <Col xs={12} sm={8} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">Total Calls</span>}
                                        value={callStats.totalCalls}
                                        prefix={<PhoneFilled style={{ color: '#722ed1', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#722ed1', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={8} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">Answered</span>}
                                        value={callStats.answeredCalls}
                                        prefix={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={8} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">Answer Rate</span>}
                                        value={callStats.answerRate}
                                        suffix="%"
                                        prefix={<RiseOutlined style={{ color: '#1890ff', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={8} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">SLA Performance</span>}
                                        value={callStats.avgSLA}
                                        suffix="%"
                                        prefix={<AlertOutlined style={{ color: parseFloat(callStats.avgSLA) >= 80 ? '#52c41a' : '#ff4d4f', fontSize: '16px' }} />}
                                        valueStyle={{ color: parseFloat(callStats.avgSLA) >= 80 ? '#52c41a' : '#ff4d4f', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Average Metrics Cards */}
                        <div id="time-metrics" className="mb-3 scroll-mt-4">
                            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">⏱️ Average Time Metrics</h3>
                        </div>
                        <Row gutter={[12, 12]} className="mb-4">
                            <Col xs={12} sm={8} lg={4} xl={4}>
                                <Card className="text-center bg-gradient-to-br from-blue-50 to-blue-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-blue-700">Avg Handling Time</span>}
                                        value={callStats.avgHandlingTime}
                                        suffix="s"
                                        prefix={<CustomerServiceOutlined style={{ color: '#1890ff', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={8} lg={4} xl={4}>
                                <Card className="text-center bg-gradient-to-br from-green-50 to-green-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-green-700">Avg Answer Time</span>}
                                        value={callStats.avgAnswerTime}
                                        suffix="s"
                                        prefix={<ClockCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={8} lg={4} xl={4}>
                                <Card className="text-center bg-gradient-to-br from-yellow-50 to-yellow-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-yellow-700">Avg Speed to Answer</span>}
                                        value={callStats.avgSpeedToAnswer}
                                        suffix="s"
                                        prefix={<RiseOutlined style={{ color: '#faad14', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#faad14', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={8} lg={4} xl={4}>
                                <Card className="text-center bg-gradient-to-br from-purple-50 to-purple-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-purple-700">Avg Talk Time</span>}
                                        value={callStats.avgTalkTime}
                                        suffix="s"
                                        prefix={<SoundOutlined style={{ color: '#722ed1', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#722ed1', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={8} lg={4} xl={4}>
                                <Card className="text-center bg-gradient-to-br from-orange-50 to-orange-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-orange-700">Avg Hold Time</span>}
                                        value={callStats.avgHoldTime}
                                        suffix="s"
                                        prefix={<PauseCircleOutlined style={{ color: '#fa8c16', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#fa8c16', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={8} lg={4} xl={4}>
                                <Card className="text-center bg-gradient-to-br from-red-50 to-red-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-red-700">Avg Wrapup Time</span>}
                                        value={callStats.avgWrapupTime}
                                        suffix="s"
                                        prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#ff4d4f', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={8}>
                                <Card className="h-full" bodyStyle={{ padding: '16px' }}>
                                    <DonutChart
                                        title="Call Distribution"
                                        size={140}
                                        data={[
                                            { name: 'Answered', value: callStats.answeredCalls, color: '#52c41a' },
                                            { name: 'Unanswered', value: callStats.unansweredCalls, color: '#ff4d4f' },
                                        ]}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} md={8}>
                                <HorizontalBarChart
                                    title="Inbound vs Outbound"
                                    height={140}
                                    data={[
                                        { name: 'Inbound', value: callStats.inboundCalls, color: '#52c41a' },
                                        { name: 'In-Answer', value: callStats.inboundAnswered, color: '#73d13d' },
                                        { name: 'Outbound', value: callStats.outboundCalls, color: '#1890ff' },
                                        { name: 'Out-Answer', value: callStats.outboundAnswered, color: '#40a9ff' },
                                    ]}
                                />
                            </Col>
                            <Col xs={24} md={8}>
                                <Card className="h-full" bodyStyle={{ padding: '16px' }}>
                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-3 text-center">
                                        Performance Summary
                                    </h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between p-2 bg-gray-50 rounded text-xs">
                                            <span>Answer Rate</span>
                                            <span className="font-semibold">{callStats.answerRate}%</span>
                                        </div>
                                        <div className="flex justify-between p-2 bg-gray-50 rounded text-xs">
                                            <span>Response Time</span>
                                            <span className="font-semibold">{callStats.avgAnswerTime}s</span>
                                        </div>
                                        <div className="flex justify-between p-2 bg-gray-50 rounded text-xs">
                                            <span>SLA Met</span>
                                            <span className={`font-semibold ${parseFloat(callStats.avgSLA) >= 80 ? 'text-green-600' : 'text-orange-600'}`}>
                                                {callStats.avgSLA}%
                                            </span>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                )}

                {/* Call Trends */}
                {trendStats && (
                    <div id="call-trends" className="mb-4 scroll-mt-4">
                        <div className="flex items-center gap-2 mb-3">
                            <RiseOutlined className="text-blue-500 text-lg" />
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Call Trends</h2>
                            <div className="text-xs text-gray-500">Yesterday vs Today</div>
                        </div>

                        {/* Comparison Summary Cards */}
                        <Row gutter={[12, 12]} className="mb-4">
                            <Col xs={12} sm={6}>
                                <Card className="text-center bg-gradient-to-br from-green-50 to-green-100" bodyStyle={{ padding: '12px' }}>
                                    <div className="text-xs text-green-700 mb-1">Today's Calls</div>
                                    <div className="text-2xl font-bold text-green-800 mb-1">
                                        <AnimatedCounter value={trendStats.todayTotal} />
                                    </div>
                                    <div className="text-xs text-green-600">
                                        Answer Rate: {trendStats.answerRateToday}%
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card className="text-center bg-gradient-to-br from-blue-50 to-blue-100" bodyStyle={{ padding: '12px' }}>
                                    <div className="text-xs text-blue-700 mb-1">Yesterday's Calls</div>
                                    <div className="text-2xl font-bold text-blue-800 mb-1">
                                        <AnimatedCounter value={trendStats.yesterdayTotal} />
                                    </div>
                                    <div className="text-xs text-blue-600">
                                        Answer Rate: {trendStats.answerRateYesterday}%
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card className="text-center bg-gradient-to-br from-purple-50 to-purple-100" bodyStyle={{ padding: '12px' }}>
                                    <div className="text-xs text-purple-700 mb-1">Growth Rate</div>
                                    <div className={`text-2xl font-bold mb-1 ${trendStats.growthRate >= 0 ? 'text-green-800' : 'text-red-800'
                                        }`}>
                                        {trendStats.growthRate >= 0 ? '+' : ''}{trendStats.growthRate}%
                                    </div>
                                    <div className="text-xs text-purple-600">
                                        {trendStats.growthRate >= 0 ? '📈 Increasing' : '📉 Decreasing'}
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card className="text-center bg-gradient-to-br from-orange-50 to-orange-100" bodyStyle={{ padding: '12px' }}>
                                    <div className="text-xs text-orange-700 mb-1">Difference</div>
                                    <div className={`text-2xl font-bold mb-1 ${trendStats.todayTotal >= trendStats.yesterdayTotal ? 'text-green-800' : 'text-red-800'
                                        }`}>
                                        {trendStats.todayTotal >= trendStats.yesterdayTotal ? '+' : ''}
                                        {trendStats.todayTotal - trendStats.yesterdayTotal}
                                    </div>
                                    <div className="text-xs text-orange-600">calls vs yesterday</div>
                                </Card>
                            </Col>
                        </Row>

                        {/* Hourly Trend Chart */}
                        <Row gutter={[12, 12]}>
                            <Col xs={24}>
                                <Card className="h-full" bodyStyle={{ padding: '16px' }}>
                                    <TrendChart
                                        title="📊 Call Volume Trends - Yesterday vs Today"
                                        data={trendStats.hourlyComparison}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Summary Cards Row */}
                        <Row gutter={[12, 12]} className="mt-4">
                            <Col xs={24} sm={12} lg={8}>
                                <Card className="h-full" bodyStyle={{ padding: '16px' }}>
                                    <DonutChart
                                        title="Total Calls Distribution"
                                        size={140}
                                        data={[
                                            { name: 'Today', value: trendStats.todayTotal, color: '#52c41a' },
                                            { name: 'Yesterday', value: trendStats.yesterdayTotal, color: '#1890ff' },
                                        ]}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={8}>
                                <Card className="h-full bg-gradient-to-br from-blue-50 to-blue-100" bodyStyle={{ padding: '16px' }}>
                                    <h3 className="text-sm font-semibold text-gray-800 mb-3 text-center">Peak Hours</h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between p-3 bg-white rounded-lg shadow-sm">
                                            <span className="text-sm text-gray-600">Today's Peak</span>
                                            <span className="font-bold text-green-600 text-lg">
                                                {trendStats.hourlyComparison.reduce((max, item) =>
                                                    item.today > max.today ? item : max
                                                ).hour}
                                            </span>
                                        </div>
                                        <div className="flex justify-between p-3 bg-white rounded-lg shadow-sm">
                                            <span className="text-sm text-gray-600">Yesterday's Peak</span>
                                            <span className="font-bold text-blue-600 text-lg">
                                                {trendStats.hourlyComparison.reduce((max, item) =>
                                                    item.yesterday > max.yesterday ? item : max
                                                ).hour}
                                            </span>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={8}>
                                <Card className="h-full bg-gradient-to-br from-purple-50 to-purple-100" bodyStyle={{ padding: '16px' }}>
                                    <h3 className="text-sm font-semibold text-gray-800 mb-3 text-center">Trend Analysis</h3>
                                    <div className="space-y-3">
                                        <div className="flex justify-between p-3 bg-white rounded-lg shadow-sm">
                                            <span className="text-sm text-gray-600">Overall Trend</span>
                                            <span className={`font-bold text-lg ${trendStats.growthRate >= 0 ? 'text-green-600' : 'text-red-600'
                                                }`}>
                                                {trendStats.growthRate >= 0 ? '📈 +' : '📉 '}{Math.abs(trendStats.growthRate)}%
                                            </span>
                                        </div>
                                        <div className="flex justify-between p-3 bg-white rounded-lg shadow-sm">
                                            <span className="text-sm text-gray-600">Status</span>
                                            <span className={`font-semibold ${trendStats.growthRate >= 0 ? 'text-green-600' : 'text-red-600'
                                                }`}>
                                                {trendStats.growthRate >= 0 ? 'Improving' : 'Declining'}
                                            </span>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                )}

                {/* Performance Metrics */}
                {performanceStats && (
                    <div id="performance-metrics" className="mb-4 scroll-mt-4">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertOutlined className="text-red-500 text-lg" />
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Performance Metrics</h2>
                            <div className="text-xs text-gray-500">Operational KPIs & Quality</div>
                        </div>

                        {/* Key Performance Indicators */}
                        <Row gutter={[12, 12]} className="mb-4">
                            <Col xs={12} sm={6} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">SLA Performance</span>}
                                        value={performanceStats.kpis.slaPerformance}
                                        suffix="%"
                                        prefix={<AlertOutlined style={{ color: performanceStats.kpis.slaPerformance >= 80 ? '#52c41a' : '#ff4d4f', fontSize: '16px' }} />}
                                        valueStyle={{ color: performanceStats.kpis.slaPerformance >= 80 ? '#52c41a' : '#ff4d4f', fontSize: '18px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">Connection Rate</span>}
                                        value={performanceStats.kpis.callConnectionRatio}
                                        suffix="%"
                                        prefix={<CheckCircleOutlined style={{ color: performanceStats.kpis.callConnectionRatio >= 70 ? '#52c41a' : '#faad14', fontSize: '16px' }} />}
                                        valueStyle={{ color: performanceStats.kpis.callConnectionRatio >= 70 ? '#52c41a' : '#faad14', fontSize: '18px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">Abandon Rate</span>}
                                        value={performanceStats.kpis.callAbandonedRatio}
                                        suffix="%"
                                        prefix={<DisconnectOutlined style={{ color: performanceStats.kpis.callAbandonedRatio <= 10 ? '#52c41a' : '#ff4d4f', fontSize: '16px' }} />}
                                        valueStyle={{ color: performanceStats.kpis.callAbandonedRatio <= 10 ? '#52c41a' : '#ff4d4f', fontSize: '18px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">Avg Handling Time</span>}
                                        value={performanceStats.kpis.avgHandlingTime}
                                        suffix="s"
                                        prefix={<ClockCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#1890ff', fontSize: '18px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">Avg Wait Time</span>}
                                        value={performanceStats.kpis.avgWaitTime}
                                        suffix="s"
                                        prefix={<ClockCircleOutlined style={{ color: performanceStats.kpis.avgWaitTime <= 30 ? '#52c41a' : '#faad14', fontSize: '16px' }} />}
                                        valueStyle={{ color: performanceStats.kpis.avgWaitTime <= 30 ? '#52c41a' : '#faad14', fontSize: '18px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">Queued Calls</span>}
                                        value={performanceStats.kpis.totalQueuedCalls}
                                        prefix={<PhoneOutlined style={{ color: '#722ed1', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#722ed1', fontSize: '18px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">Longest Queue</span>}
                                        value={performanceStats.kpis.longestQueueTime}
                                        suffix="s"
                                        prefix={<ExclamationCircleOutlined style={{ color: performanceStats.kpis.longestQueueTime <= 60 ? '#52c41a' : '#ff4d4f', fontSize: '16px' }} />}
                                        valueStyle={{ color: performanceStats.kpis.longestQueueTime <= 60 ? '#52c41a' : '#ff4d4f', fontSize: '18px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4} xl={3}>
                                <Card className="text-center" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs">Wrap Exceeded</span>}
                                        value={performanceStats.kpis.wrapTimeExceeded}
                                        suffix="%"
                                        prefix={<PauseCircleOutlined style={{ color: performanceStats.kpis.wrapTimeExceeded <= 20 ? '#52c41a' : '#faad14', fontSize: '16px' }} />}
                                        valueStyle={{ color: performanceStats.kpis.wrapTimeExceeded <= 20 ? '#52c41a' : '#faad14', fontSize: '18px' }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Performance Charts */}
                        <Row gutter={[12, 12]} className="mb-4">
                            <Col xs={24} lg={16}>
                                <Card className="h-full" bodyStyle={{ padding: '16px' }}>
                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-4 text-center">
                                        📊 Hourly Performance Overview
                                    </h3>
                                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                                        <ResponsiveContainer width="100%" height={350}>
                                            <BarChart
                                                data={performanceStats.hourlyChart}
                                                margin={{
                                                    top: 20,
                                                    right: 30,
                                                    left: 20,
                                                    bottom: 60,
                                                }}
                                                barCategoryGap="20%"
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                <XAxis
                                                    dataKey="hour"
                                                    tick={{ fontSize: 11, fill: '#666' }}
                                                    axisLine={{ stroke: '#d1d5db' }}
                                                    tickLine={{ stroke: '#d1d5db' }}
                                                    label={{
                                                        value: 'Hour',
                                                        position: 'insideBottom',
                                                        offset: -5,
                                                        style: { textAnchor: 'middle', fontSize: '11px', fill: '#666' }
                                                    }}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 11, fill: '#666' }}
                                                    axisLine={{ stroke: '#d1d5db' }}
                                                    tickLine={{ stroke: '#d1d5db' }}
                                                    label={{
                                                        value: 'Calls',
                                                        angle: -90,
                                                        position: 'insideLeft',
                                                        style: { textAnchor: 'middle', fontSize: '11px', fill: '#666' }
                                                    }}
                                                />
                                                <RechartsTooltip
                                                    content={({ active, payload, label }: any) => {
                                                        if (active && payload && payload.length) {
                                                            return (
                                                                <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
                                                                    <p className="font-semibold text-gray-800 mb-2">{`Hour: ${label}`}</p>
                                                                    {payload.map((entry: any, index: number) => (
                                                                        <p key={index} style={{ color: entry.color }} className="font-medium text-sm">
                                                                            {`${entry.dataKey}: ${entry.value}${entry.dataKey.includes('sla') || entry.dataKey.includes('Ratio') ? '%' : ''}`}
                                                                        </p>
                                                                    ))}
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    }}
                                                />
                                                <Legend
                                                    wrapperStyle={{
                                                        paddingTop: '15px',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                                <Bar
                                                    dataKey="calls"
                                                    fill="#3b82f6"
                                                    radius={[3, 3, 0, 0]}
                                                    name="Total Calls"
                                                />
                                                <Bar
                                                    dataKey="answered"
                                                    fill="#10b981"
                                                    radius={[3, 3, 0, 0]}
                                                    name="Answered"
                                                />
                                                <Bar
                                                    dataKey="abandoned"
                                                    fill="#f59e0b"
                                                    radius={[3, 3, 0, 0]}
                                                    name="Abandoned"
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={24} lg={8}>
                                <Card className="h-full" bodyStyle={{ padding: '16px' }}>
                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-4 text-center">
                                        📈 Performance Summary
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 rounded-lg">
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs font-medium text-green-700">SLA Performance</span>
                                                <span className={`text-sm font-bold ${performanceStats.kpis.slaPerformance >= 80 ? 'text-green-800' : 'text-red-600'}`}>
                                                    {performanceStats.kpis.slaPerformance}%
                                                </span>
                                            </div>
                                            <div className="mt-2">
                                                <Progress
                                                    percent={performanceStats.kpis.slaPerformance}
                                                    strokeColor={performanceStats.kpis.slaPerformance >= 80 ? '#10b981' : '#ef4444'}
                                                    trailColor="#f3f4f6"
                                                    showInfo={false}
                                                    size="small"
                                                />
                                            </div>
                                        </div>

                                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg">
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs font-medium text-blue-700">Connection Rate</span>
                                                <span className="text-sm font-bold text-blue-800">{performanceStats.kpis.callConnectionRatio}%</span>
                                            </div>
                                            <div className="mt-2">
                                                <Progress
                                                    percent={performanceStats.kpis.callConnectionRatio}
                                                    strokeColor="#3b82f6"
                                                    trailColor="#f3f4f6"
                                                    showInfo={false}
                                                    size="small"
                                                />
                                            </div>
                                        </div>

                                        <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-3 rounded-lg">
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs font-medium text-orange-700">Queue Health</span>
                                                <span className="text-sm font-bold text-orange-800">
                                                    {performanceStats.kpis.longestQueueTime <= 60 ? 'Good' : 'Needs Attention'}
                                                </span>
                                            </div>
                                            <div className="text-xs text-orange-600 mt-1">
                                                Max wait: {performanceStats.kpis.longestQueueTime}s | Queued: {performanceStats.kpis.totalQueuedCalls}
                                            </div>
                                        </div>

                                        <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-3 rounded-lg">
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs font-medium text-purple-700">Efficiency Score</span>
                                                <span className="text-sm font-bold text-purple-800">
                                                    {((100 - performanceStats.kpis.callAbandonedRatio) * (performanceStats.kpis.callConnectionRatio / 100)).toFixed(0)}%
                                                </span>
                                            </div>
                                            <div className="text-xs text-purple-600 mt-1">
                                                Based on connection & abandon rates
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                )}

                {/* Agent Performance */}
                {agentPerformanceStats && (
                    <div id="agent-performance" className="mb-4 scroll-mt-4">
                        <div className="flex items-center gap-2 mb-3">
                            <UserOutlined className="text-cyan-500 text-lg" />
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Agent Performance</h2>
                            <div className="text-xs text-gray-500">Individual agent status & metrics</div>
                        </div>

                        {/* Agent Summary Cards */}
                        <Row gutter={[12, 12]} className="mb-4">
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-cyan-50 to-cyan-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-cyan-700">Total Agents</span>}
                                        value={agentPerformanceStats.stats.totalAgents}
                                        prefix={<TeamOutlined style={{ color: '#06b6d4', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#06b6d4', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-green-50 to-green-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-green-700">Ready</span>}
                                        value={agentPerformanceStats.stats.ready}
                                        prefix={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-orange-50 to-orange-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-orange-700">Busy</span>}
                                        value={agentPerformanceStats.stats.busy}
                                        prefix={<SoundOutlined style={{ color: '#fa8c16', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#fa8c16', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-red-50 to-red-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-red-700">Offline</span>}
                                        value={agentPerformanceStats.stats.offline}
                                        prefix={<DisconnectOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#ff4d4f', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-blue-50 to-blue-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-blue-700">Manual Mode</span>}
                                        value={agentPerformanceStats.stats.manual}
                                        prefix={<PlayCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6} lg={4}>
                                <Card className="text-center bg-gradient-to-br from-purple-50 to-purple-100" bodyStyle={{ padding: '12px' }}>
                                    <Statistic
                                        title={<span className="text-xs text-purple-700">Call Answered</span>}
                                        value={agentPerformanceStats.stats.answered}
                                        prefix={<PhoneFilled style={{ color: '#722ed1', fontSize: '16px' }} />}
                                        valueStyle={{ color: '#722ed1', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Agent Distribution Charts */}
                        <Row gutter={[12, 12]} className="mb-4">
                            <Col xs={24} md={8}>
                                <Card className="h-full" bodyStyle={{ padding: '16px' }}>
                                    <DonutChart
                                        title="Agent Status Distribution"
                                        size={140}
                                        data={[
                                            { name: 'Ready', value: agentPerformanceStats.stats.ready, color: '#52c41a' },
                                            { name: 'Busy', value: agentPerformanceStats.stats.busy, color: '#fa8c16' },
                                            { name: 'Offline', value: agentPerformanceStats.stats.offline, color: '#ff4d4f' },
                                        ]}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} md={8}>
                                <Card className="h-full" bodyStyle={{ padding: '16px' }}>
                                    <DonutChart
                                        title="Agent Mode Distribution"
                                        size={140}
                                        data={[
                                            { name: 'Manual', value: agentPerformanceStats.stats.manual, color: '#1890ff' },
                                            { name: 'Inbound', value: agentPerformanceStats.stats.inbound, color: '#52c41a' },
                                        ]}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} md={8}>
                                <Card className="h-full" bodyStyle={{ padding: '16px' }}>
                                    <DonutChart
                                        title="Call Status Distribution"
                                        size={140}
                                        data={[
                                            { name: 'Answered', value: agentPerformanceStats.stats.answered, color: '#52c41a' },
                                            { name: 'Not Answered', value: agentPerformanceStats.stats.notAnswered, color: '#ff4d4f' },
                                        ]}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Individual Agent Performance Table */}
                        <Row gutter={[12, 12]}>
                            <Col xs={24}>
                                <Card>
                                    <div className="flex items-center gap-2 mb-4">
                                        <CustomerServiceOutlined className="text-blue-500" />
                                        <h3 className="text-md font-semibold text-gray-800 dark:text-white">Individual Agent Status</h3>
                                    </div>

                                    <Table
                                        dataSource={agentPerformanceStats.agents}
                                        rowKey="AgentUniqueId"
                                        size="small"
                                        scroll={{ x: 800 }}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showQuickJumper: true,
                                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} agents`,
                                        }}
                                        columns={[
                                            {
                                                title: 'Status',
                                                dataIndex: 'AgentState',
                                                key: 'status',
                                                width: 100,
                                                render: (status: string, record: any) => (
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className="w-3 h-3 rounded-full"
                                                            style={{ backgroundColor: record.statusColor }}
                                                        />
                                                        <Tag color={status === 'READY' ? 'green' : status === 'BUSY' ? 'orange' : 'red'}>
                                                            {status}
                                                        </Tag>
                                                    </div>
                                                ),
                                                filters: [
                                                    { text: 'READY', value: 'READY' },
                                                    { text: 'BUSY', value: 'BUSY' },
                                                    { text: 'OFFLINE', value: 'OFFLINE' },
                                                ],
                                                onFilter: (value, record) => record.AgentState === value,
                                            },
                                            {
                                                title: 'Agent Name',
                                                dataIndex: 'AgentName',
                                                key: 'name',
                                                width: 120,
                                                render: (name: string) => (
                                                    <span className="font-semibold text-gray-800">{name}</span>
                                                ),
                                                sorter: (a, b) => a.AgentName.localeCompare(b.AgentName),
                                            },
                                            {
                                                title: 'Agent ID',
                                                dataIndex: 'AgentId',
                                                key: 'id',
                                                width: 80,
                                                render: (id: string) => (
                                                    <span className="font-mono text-sm">{id}</span>
                                                ),
                                            },
                                            {
                                                title: 'Mode',
                                                dataIndex: 'AgentMode',
                                                key: 'mode',
                                                width: 100,
                                                render: (mode: string) => (
                                                    <Badge
                                                        status={mode === 'MANUAL' ? 'processing' : 'default'}
                                                        text={mode}
                                                    />
                                                ),
                                                filters: [
                                                    { text: 'MANUAL', value: 'MANUAL' },
                                                    { text: 'INBOUND', value: 'INBOUND' },
                                                ],
                                                onFilter: (value, record) => record.AgentMode === value,
                                            },
                                            {
                                                title: 'Phone',
                                                dataIndex: 'AgentPhoneNumber',
                                                key: 'phone',
                                                width: 120,
                                                render: (phone: string) => (
                                                    <span className="font-mono text-sm">{phone}</span>
                                                ),
                                            },
                                            {
                                                title: 'Skills',
                                                dataIndex: 'AgentSkills',
                                                key: 'skills',
                                                width: 80,
                                                render: (skills: string, record: any) => (
                                                    <Tooltip title={skills} placement="top">
                                                        <span className="font-medium text-blue-600 cursor-help">
                                                            {record.skillsCount} skills
                                                        </span>
                                                    </Tooltip>
                                                ),
                                                sorter: (a, b) => a.skillsCount - b.skillsCount,
                                            },
                                            {
                                                title: 'Call Status',
                                                dataIndex: 'CallStatus',
                                                key: 'callStatus',
                                                width: 120,
                                                render: (status: string) => {
                                                    if (!status) return '-';
                                                    return (
                                                        <Tag
                                                            color={status === 'answered' ? 'green' : status === 'not_answered' ? 'red' : 'orange'}
                                                        >
                                                            {status.replace('_', ' ')}
                                                        </Tag>
                                                    );
                                                },
                                                filters: [
                                                    { text: 'Answered', value: 'answered' },
                                                    { text: 'Not Answered', value: 'not_answered' },
                                                    { text: 'Agent Disconnect', value: 'agent_disconnect' },
                                                    { text: 'Congestion', value: 'Congestion' },
                                                ],
                                                onFilter: (value, record) => record.CallStatus === value,
                                            },
                                            {
                                                title: 'Customer',
                                                dataIndex: 'CustomerPhoneNumber',
                                                key: 'customer',
                                                width: 120,
                                                render: (phone: string) => {
                                                    if (!phone) return '-';
                                                    return (
                                                        <span className="font-mono text-sm text-green-600">{phone}</span>
                                                    );
                                                },
                                            },
                                            {
                                                title: 'Since',
                                                dataIndex: 'timeSince',
                                                key: 'since',
                                                width: 80,
                                                render: (time: number) => (
                                                    <span className="font-medium text-orange-600">
                                                        {time}m ago
                                                    </span>
                                                ),
                                                sorter: (a, b) => a.timeSince - b.timeSince,
                                            },
                                        ]}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </div>
                )}
            </div>
        </StitchLayout>
    );
};

export default OzonetelDashboard;
